<template>
  <div class="comments-block">
    <h3 class="comments-block-title">
      评论<span v-if="commentTotal > 0" class="comments-count">{{ commentTotal }}</span>
    </h3>
    <div class="comments-container">
      <textarea
        v-model="commentContent"
        class="comment-input"
        placeholder="留下你的评论..."
        @focus="commentInputFocused = true"
        @blur="commentInputFocused = false"
      />
      <button
        v-show="commentInputFocused || commentContent"
        class="add-comment-button"
        :disabled="submittingComment"
        @click="submitComment"
      >
        {{ submittingComment ? '发布中...' : '发布评论' }}
      </button>
      <div class="comments-section">
        <div
          class="comment"
          v-for="comment in comments"
          :key="comment.messageId"
          :id="'comic-comment-' + (comment.messageId || comment.id)"
        >
          <div class="comment-content-wrapper">
            <img
              :src="comment.avatar || PLACEHOLDER_ASSETS.defaultAvatar"
              alt="用户头像"
              class="comment-avatar"
            />
            <div class="comment-main">
              <p class="comment-author">{{ comment.userName }}</p>
              <p class="comment-text">{{ comment.content }}</p>
              <div class="comment-actions">
                <div class="comment-actions-left">
                  <span class="comment-time">{{ comment.addTime }}</span>
                  <span class="comment-reply-btn" @click="toggleReplyBox(comment.messageId)">
                    回复
                  </span>
                  <span
                    v-if="comment.isBelongMe"
                    class="comment-delete-btn"
                    @click.stop="deleteComicComment(comment)"
                  >
                    删除
                  </span>
                </div>
                <div class="comment-actions-right">
                  <span
                    class="comment-like"
                    :class="{ 'is-liked': comment.isMyLike }"
                    @click="likeComment(comment)"
                  >
                    <AppIcon
                      name="like"
                      size="sm"
                      :variant="comment.isMyLike ? 'active' : 'default'"
                      aria-label="点赞评论"
                    />
                    <span>{{ comment.likeCount || 0 }}</span>
                    <span v-if="likeEffectMap[comment.messageId]" class="like-effect">+1</span>
                  </span>
                </div>
              </div>
              <div v-if="showReplyBoxMap[comment.messageId]" class="comment-reply-box">
                <textarea
                  v-model="replyContentMap[comment.messageId]"
                  class="comment-reply-input"
                  placeholder="请输入回复内容..."
                />
                <div class="comment-reply-actions">
                  <button
                    class="comment-reply-submit"
                    :disabled="submittingReplyMap[comment.messageId]"
                    @click="submitCommentReply(comment)"
                  >
                    {{ submittingReplyMap[comment.messageId] ? '提交中...' : '提交' }}
                  </button>
                  <button class="comment-reply-cancel" @click="toggleReplyBox(comment.messageId)">
                    取消
                  </button>
                </div>
              </div>
              <div
                v-if="comment.existReply"
                class="comment-expand-reply"
                :class="{ 'is-loading': pendingExpandRepliesMap[comment.messageId] }"
                @click="pendingExpandRepliesMap[comment.messageId] ? null : toggleCommentReplies(comment)"
              >
                <template v-if="pendingExpandRepliesMap[comment.messageId]">
                  <AppIcon name="refresh" size="sm" class="reply-loading-icon app-icon--spin" aria-label="" />
                  加载中...
                </template>
                <template v-else>
                  {{ comment.showReplies ? '收起' : '展开' }}{{ comment.replyCount || 0 }}条回复
                </template>
              </div>
              <div
                v-if="comment.showReplies && comment.replies && comment.replies.length > 0"
                class="comment-replies-list"
              >
                <div
                  v-for="reply in (showAllRepliesMap[comment.messageId] ? comment.replies : comment.replies.slice(0, 3))"
                  :key="reply.messageId"
                  class="comment-reply-item"
                  :id="'comic-comment-' + (reply.messageId || reply.id)"
                >
                  <div class="comment-reply-header">
                    <img
                      :src="reply.avatar || PLACEHOLDER_ASSETS.defaultAvatar"
                      alt="用户头像"
                      class="comment-reply-avatar"
                    />
                    <span class="comment-reply-author">{{ reply.userName }}</span>
                    <span v-if="reply.toUserName" class="comment-reply-to-info">
                      <span class="comment-reply-to-text">回复</span>
                      <span class="comment-reply-to-name">{{ reply.toUserName }}</span>
                    </span>
                  </div>
                  <p class="comment-reply-content">{{ reply.content }}</p>
                  <div class="comment-reply-actions">
                    <div class="comment-reply-actions-left">
                      <span class="comment-reply-time">{{ reply.replyTime }}</span>
                      <span class="comment-reply-btn-inline" @click="toggleReplyBox(reply.messageId)">
                        回复
                      </span>
                      <span
                        v-if="reply.isBelongMe"
                        class="comment-delete-btn"
                        @click.stop="deleteComicReply(comment, reply)"
                      >
                        删除
                      </span>
                    </div>
                    <div class="comment-reply-actions-right">
                      <span
                        class="comment-reply-like"
                        :class="{ 'is-liked': reply.isMyLike }"
                        @click="likeReply(reply)"
                      >
                        <AppIcon
                          name="like"
                          size="sm"
                          :variant="reply.isMyLike ? 'active' : 'default'"
                          aria-label="点赞回复"
                        />
                        <span>{{ reply.likeCount || 0 }}</span>
                        <span v-if="likeEffectMap[reply.messageId]" class="like-effect">+1</span>
                      </span>
                    </div>
                  </div>
                  <div v-if="showReplyBoxMap[reply.messageId]" class="comment-reply-box">
                    <textarea
                      v-model="replyContentMap[reply.messageId]"
                      class="comment-reply-input"
                      placeholder="请输入回复内容..."
                    />
                    <div class="comment-reply-actions">
                      <button
                        class="comment-reply-submit"
                        :disabled="submittingReplyMap[reply.messageId]"
                        @click="submitCommentReply(reply)"
                      >
                        {{ submittingReplyMap[reply.messageId] ? '提交中...' : '提交' }}
                      </button>
                      <button class="comment-reply-cancel" @click="toggleReplyBox(reply.messageId)">
                        取消
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="comment.replies.length > 3" class="comment-reply-expand">
                  <span
                    class="comment-reply-expand-btn"
                    @click="toggleShowAllReplies(comment.messageId)"
                  >
                    {{ showAllRepliesMap[comment.messageId] ? '收起' : '展开更多' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="comment-pagination" v-if="commentTotal > 0 && commentPages > 0">
          <Pagination
            :key="`comment-pagination-${commentPages}-${commentPageNum}`"
            :total-pages="commentPages"
            :current-page="commentPageNum"
            :total-count="commentTotal"
            :page-size="commentPageSize"
            :show-page-size-selector="false"
            :show-jump-to-page="false"
            unit="条"
            @change-page="handleCommentPageChange"
          />
        </div>
        <div class="no-comments" v-if="comments.length === 0 && !loadingComments">
          <p>暂无评论，快来发表第一条评论吧！</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import Pagination from '@/shared/components/Pagination.vue'
import AppIcon from '@/shared/components/AppIcon.vue'
import { PLACEHOLDER_ASSETS } from '@/shared/assets/manifest'

const commentContent = defineModel('commentContent', { type: String, default: '' })
const commentInputFocused = defineModel('commentInputFocused', { type: Boolean, default: false })

defineProps({
  comments: { type: Array, default: () => [] },
  commentTotal: { type: Number, default: 0 },
  commentPages: { type: Number, default: 0 },
  commentPageNum: { type: Number, default: 1 },
  commentPageSize: { type: Number, default: 5 },
  loadingComments: { type: Boolean, default: false },
  submittingComment: { type: Boolean, default: false },
  replyContentMap: { type: Object, default: () => ({}) },
  showReplyBoxMap: { type: Object, default: () => ({}) },
  submittingReplyMap: { type: Object, default: () => ({}) },
  pendingExpandRepliesMap: { type: Object, default: () => ({}) },
  showAllRepliesMap: { type: Object, default: () => ({}) },
  likeEffectMap: { type: Object, default: () => ({}) },
  submitComment: { type: Function, required: true },
  toggleReplyBox: { type: Function, required: true },
  submitCommentReply: { type: Function, required: true },
  toggleCommentReplies: { type: Function, required: true },
  toggleShowAllReplies: { type: Function, required: true },
  deleteComicComment: { type: Function, required: true },
  deleteComicReply: { type: Function, required: true },
  likeComment: { type: Function, required: true },
  likeReply: { type: Function, required: true },
  handleCommentPageChange: { type: Function, required: true },
})
</script>
